import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';
import { PullRequest } from 'src/types/pull-requests-table';

export type MergeQueues = MergeQueue[];

export enum MergeQueueStrategy {
  OPTIMISTIC_BATCHED_PR = 'OPTIMISTIC_BATCHED_PR',
  STRICT_BATCHED_PR = 'STRICT_BATCHED_PR',
  STRICT_PER_PR = 'STRICT_PER_PR',
}

export enum MergeQueueBranchMode {
  BRANCH_NAME = 'BRANCH_NAME',
  BRANCH_TYPE = 'BRANCH_TYPE',
  BRANCH_PATTERN = 'BRANCH_PATTERN',
}

export type MergeQueueBranch = {
  mode: MergeQueueBranchMode;
  name?: string;
  type?: string;
  pattern?: string;
};

export type MergeQueuePayload = {
  name: string;
  strategy: MergeQueueStrategy;
  branch: MergeQueueBranch;
  is_frozen: boolean;
  build_concurrency: number;
  maximum_wait_time_in_seconds: number;
  maximum_batch_wait_time_in_seconds?: number;
  minimum_pull_requests_in_batch?: number;
  maximum_pull_requests_in_batch?: number;
};

export type MergeQueueResponse = MergeQueuePayload & {
  type: 'merge-queue';
  uuid: string;
  created_on: string;
  last_updated_on: string;
  build_concurrency: number;
};

export type MergeQueuesResourceResponse = {
  page: number;
  pagelen: number;
  size: number;
  values: MergeQueues;
};

export type MergeQueuesProps = {
  mergeQueues: MergeQueuesResourceResponse | null;
  mergeQueuesLoading: boolean;
};

export type MergeQueuesInlineSettingsProps = {
  isFrozen: boolean;
  onEditClick: () => void;
  onViewClick: () => void;
  onFreezeToggle: () => void;
  onRemoveClick: () => void;
};

export type MergeQueuesState = {
  isLoading: boolean;
  isCreateEditModalOpen: boolean;
  isCreateEditModalLoading: boolean;
  isRemoveModalOpen: boolean;
  isMergeQueuePullRequestsModalOpen: boolean;
  isEmpty: boolean;
  mergeQueues: MergeQueues;
  focusedMergeQueue?: MergeQueue;
};

export enum WaitTimeUnit {
  SECONDS = 'SECONDS',
  MINUTES = 'MINUTES',
  HOURS = 'HOURS',
}

export type MergeQueueFormState = {
  name: string;
  strategy: MergeQueueStrategy;
  targetBranchName: string;
  buildConcurrency: number;
  maximumWaitTime: number;
  maximumWaitTimeUnit: WaitTimeUnit;
  maximumPullRequestsInBatch?: number;
  maximumBatchWaitTime?: number;
  maximumBatchWaitTimeUnit?: WaitTimeUnit;
  minimumPullRequestsInBatch?: number;
};

export type BranchesResponse = {
  values: Array<{
    name: string;
    target: { hash: string };
  }>;
};

export type MergeQueuePullRequestStatus =
  | 'QUEUED'
  | 'PROCESSING'
  | 'MERGING'
  | 'DEQUEUING'
  | 'MERGED'
  | 'DEQUEUED';

export type MergeQueuePullRequest = {
  merge_queue_uuid: string;
  pr_id: string;
  pr_status: MergeQueuePullRequestStatus;
  pr_order: number;
  target_branch: string;
  created_on: string;
  last_updated_on: string;
};

export type MergeQueuePullRequestsParams = {
  mergeQueueUuid: string;
  owner: string;
  slug: string;
  page?: string;
};

export type MergeQueuePullRequestsResponse = {
  page: number;
  pagelen: number;
  size: number;
  values: MergeQueuePullRequest[];
};

export type QueuedPullRequestsParams = {
  owner: string;
  slug: string;
  page?: string;
  targetBranch: string;
};

export type QueuedPullRequestsResponse = {
  next: string | null;
  page: number;
  pagelen: number;
  size: number;
  values: PullRequest[];
};

export type UseQueuedPullRequestsParams = {
  mergeQueueUuid: string;
  page: string;
  owner: string;
  slug: string;
  targetBranch: string;
};

export type UseQueuedPullRequestsResult = {
  isQueuedPullRequestsLoading: boolean;
  pullRequestsInMergeQueue: PullRequest[];
  queuedPullRequestsPages: number[];
};
